import { useState, useEffect, useMemo } from 'react'
import { usePortalClient } from '../hooks/usePortalClient'
import { useClientIntake } from '../../assessment/hooks/useClientIntake'
import { INDUSTRY_HIERARCHY } from '../../../shared/data/industries'
import businessScopingData from '../../../shared/data/businessScoping.json'
import { supabase } from '../../../shared/lib/supabase'
import {
  STAFFING_SIZE_OPTIONS,
  IT_TEAM_SIZE_OPTIONS,
  SECURITY_TEAM_SIZE_OPTIONS,
  SECURITY_FUNCTION_MODEL_OPTIONS,
  ANNUAL_REVENUE_OPTIONS,
  GEOGRAPHICAL_PRESENCE_OPTIONS,
  BUSINESS_MODEL_OPTIONS,
  US_STATES
} from '../../../shared/utils/intakeFieldMapping'

// Extract engagement context questions from business scoping JSON (ed-1, ed-2, ed-3)
const engagementSection = businessScopingData.sections.find(s => s.id === 'engagement-context')
const ED_QUESTIONS = engagementSection?.questions || []

const SECTIONS = [
  { id: 'org', label: 'Organization', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
  { id: 'contact', label: 'Contacts', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
  { id: 'location', label: 'Locations', icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z' },
  { id: 'business', label: 'Business', icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  { id: 'engagement', label: 'Engagement', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' }
]

const SECTION_META = {
  org: {
    title: 'Organization Information',
    description: 'Tell us about your organization so we can benchmark against similar companies in your industry.'
  },
  contact: {
    title: 'Contact Information',
    description: 'Who should we reach out to with questions or to share deliverables?'
  },
  location: {
    title: 'Locations & Data Residency',
    description: 'Your operational footprint determines which regulatory frameworks and breach notification laws apply.'
  },
  business: {
    title: 'Business Context',
    description: 'Understanding your business model helps us tailor recommendations to your specific risk profile.'
  },
  engagement: {
    title: 'Engagement Context',
    description: 'Understanding what brings you here helps us tailor recommendations to your specific situation.'
  }
}

function PortalIntake() {
  const { client, clientId, loading: clientLoading } = usePortalClient()
  const {
    status, isEditable, isSubmitted, loading: intakeLoading,
    saving, submitting, error,
    buildInitialFormData, buildInitialEngagementContext,
    saveProgress, submitIntake, intakeRecord
  } = useClientIntake(clientId, client)

  const [formData, setFormData] = useState({})
  const [engagementData, setEngagementData] = useState({})
  const [activeSection, setActiveSection] = useState(0)
  const [initialized, setInitialized] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [saveToast, setSaveToast] = useState(false)
  const [knownFrameworks, setKnownFrameworks] = useState([])
  const [customFrameworkInput, setCustomFrameworkInput] = useState('')

  // Fetch known compliance frameworks from database
  useEffect(() => {
    supabase
      .from('compliance_frameworks')
      .select('id, name, abbreviation, category')
      .eq('is_active', true)
      .order('category')
      .order('name')
      .then(({ data }) => {
        if (data) setKnownFrameworks(data)
      })
  }, [])

  // Initialize form data from client record
  useEffect(() => {
    if (client && !initialized) {
      const initial = buildInitialFormData()
      // Pre-fill compliance_frameworks from client.regulatoryFrameworks
      initial.compliance_frameworks = client.regulatoryFrameworks || []
      initial.custom_frameworks_raw = []
      setFormData(initial)

      buildInitialEngagementContext().then(ec => {
        setEngagementData(ec)
        setInitialized(true)
      })
    }
  }, [client, initialized, buildInitialFormData, buildInitialEngagementContext])

  // Sub-industry options based on selected industry
  const subCategoryOptions = useMemo(() => {
    const industry = INDUSTRY_HIERARCHY.find(i => i.value === formData.industry)
    return industry?.subCategories || []
  }, [formData.industry])

  // Whether international locations field should show
  const showInternational = formData.geographical_presence === 'international' || formData.geographical_presence === 'global'

  // Progress calculation
  const sectionComplete = useMemo(() => {
    const orgFields = ['name', 'industry', 'staffing_size', 'annual_revenue']
    const contactFields = ['contact_name', 'contact_email']
    const locationFields = ['geographical_presence']

    return {
      org: orgFields.every(f => formData[f]),
      contact: contactFields.every(f => formData[f]),
      location: locationFields.every(f => formData[f]),
      business: !!formData.business_model,
      engagement: Array.isArray(engagementData.engagement_drivers) ? engagementData.engagement_drivers.length > 0 : !!engagementData.engagement_drivers
    }
  }, [formData, engagementData])

  const completedSections = Object.values(sectionComplete).filter(Boolean).length
  const progressPercent = Math.round((completedSections / SECTIONS.length) * 100)

  const updateField = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value }
      if (field === 'industry') {
        next.industry_sub_category = ''
      }
      return next
    })
  }

  const updateEngagement = (field, value) => {
    setEngagementData(prev => ({ ...prev, [field]: value }))
  }

  const handleSaveProgress = async () => {
    const ok = await saveProgress(formData)
    if (ok) {
      setSaveToast(true)
      setTimeout(() => setSaveToast(false), 2500)
    }
  }

  const handleSubmit = async () => {
    const success = await submitIntake(formData, engagementData)
    if (success) setSubmitted(true)
  }

  const handleNextSection = async () => {
    await saveProgress(formData)
    setActiveSection(prev => Math.min(prev + 1, SECTIONS.length - 1))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handlePrevSection = () => {
    setActiveSection(prev => Math.max(prev - 1, 0))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Loading state
  if (clientLoading || intakeLoading || !initialized) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="rounded-2xl p-8" style={{ background: 'linear-gradient(135deg, #012438 0%, #1a3a4f 100%)' }}>
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-56 rounded-lg bg-white/15" />
            <div className="h-4 w-80 rounded bg-white/10" />
          </div>
        </div>
      </div>
    )
  }

  // Post-submission confirmation
  if (submitted || (isSubmitted && !submitted)) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Success header */}
        <div className="rounded-2xl p-8 text-center" style={{ background: 'linear-gradient(135deg, #065f46 0%, #047857 100%)' }}>
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-white/15">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Profile Submitted Successfully</h2>
          <p className="text-emerald-100 max-w-md mx-auto">
            Thank you for providing your organization's information. Your advisor will review this to prepare for the security assessment.
          </p>
          {intakeRecord?.submitted_at && (
            <p className="text-emerald-200 text-sm mt-3">
              Submitted {new Date(intakeRecord.submitted_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          )}
        </div>

        {/* Read-only summary */}
        <ReadOnlySummary formData={formData} engagementData={engagementData} subCategoryOptions={subCategoryOptions} />
      </div>
    )
  }

  // Unlocked state
  const isUnlocked = status === 'unlocked'
  const currentSectionMeta = SECTION_META[SECTIONS[activeSection].id]

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Welcome header */}
      <div className="rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #012438 0%, #1a3a4f 100%)' }}>
        <div className="p-8">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Organization Profile</h2>
              <p className="text-slate-300 max-w-lg text-sm leading-relaxed">
                This information helps your advisory team prepare a tailored security assessment. All data is kept confidential and used solely for your engagement.
              </p>
            </div>
            {isUnlocked && (
              <span className="flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1.5 bg-amber-500/20 text-amber-300">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
                Open for editing
              </span>
            )}
          </div>

          {/* Progress */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-400">
                {completedSections} of {SECTIONS.length} sections complete
              </span>
              <span className="text-xs text-slate-500">{progressPercent}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/10">
              <div
                className="h-1.5 rounded-full transition-all duration-500 ease-out bg-primary-400"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>

      </div>

      {/* Step indicator */}
      <div className="flex items-center justify-between px-2">
        {SECTIONS.map((section, idx) => {
          const isActive = idx === activeSection
          const isDone = sectionComplete[section.id]
          const isPast = idx < activeSection

          return (
            <div key={section.id} className={`flex items-center ${idx < SECTIONS.length - 1 ? 'flex-1' : ''}`}>
              <button
                onClick={() => setActiveSection(idx)}
                className="flex flex-col items-center gap-1.5 group"
                title={section.label}
              >
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-all text-sm font-semibold ${
                    isActive
                      ? 'bg-primary-900 dark:bg-primary-800 text-white shadow-[0_0_0_3px_rgba(1,36,56,0.15)]'
                      : isDone
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {isDone && !isActive ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    idx + 1
                  )}
                </div>
                <span
                  className={`text-xs font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? 'text-primary-900 dark:text-white'
                      : isDone
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {section.label}
                </span>
              </button>
              {idx < SECTIONS.length - 1 && (
                <div
                  className={`h-px flex-1 mx-2 mt-[-18px] ${
                    isPast || (isDone && idx < activeSection)
                      ? 'bg-green-200 dark:bg-green-800'
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              )}
            </div>
          )
        })}
      </div>

      {/* Form card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700">
        {/* Section header */}
        <div className="px-8 pt-7 pb-5 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-cyan-50 dark:bg-cyan-900/20">
              <svg className="w-4 h-4 text-cyan-600 dark:text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={SECTIONS[activeSection].icon} />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{currentSectionMeta.title}</h3>
          </div>
          <p className="text-sm ml-11 text-gray-500 dark:text-gray-400">{currentSectionMeta.description}</p>
        </div>

        {/* Form body */}
        <div className="px-8 py-6">
          {error && (
            <div className="mb-5 flex items-center gap-2 rounded-lg p-3 text-sm bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400 border border-red-200 dark:border-red-800">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          {activeSection === 0 && (
            <SectionOrganization formData={formData} updateField={updateField} subCategoryOptions={subCategoryOptions} />
          )}
          {activeSection === 1 && (
            <SectionContact formData={formData} updateField={updateField} />
          )}
          {activeSection === 2 && (
            <SectionLocation formData={formData} updateField={updateField} showInternational={showInternational} />
          )}
          {activeSection === 3 && (
            <SectionBusiness formData={formData} updateField={updateField} knownFrameworks={knownFrameworks} customFrameworkInput={customFrameworkInput} setCustomFrameworkInput={setCustomFrameworkInput} />
          )}
          {activeSection === 4 && (
            <SectionEngagement engagementData={engagementData} updateEngagement={updateEngagement} />
          )}
        </div>

        {/* Navigation footer */}
        <div className="flex items-center justify-between px-8 py-5 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={handlePrevSection}
            disabled={activeSection === 0}
            className="flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-30 text-gray-500 dark:text-gray-400"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <div className="flex items-center gap-3">
            {/* Save toast */}
            {saveToast && (
              <span className="text-xs font-medium flex items-center gap-1 text-green-500">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Saved
              </span>
            )}

            <button
              onClick={handleSaveProgress}
              disabled={saving}
              className="text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700"
            >
              {saving ? 'Saving...' : 'Save Progress'}
            </button>

            {activeSection < SECTIONS.length - 1 ? (
              <button
                onClick={handleNextSection}
                className="flex items-center gap-1.5 text-sm font-medium px-5 py-2 rounded-lg text-white transition-colors hover:opacity-90 bg-primary-900 dark:bg-primary-800"
              >
                Continue
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting || completedSections < 3}
                className="flex items-center gap-1.5 text-sm font-medium px-5 py-2 rounded-lg text-white transition-colors hover:opacity-90 disabled:opacity-50 bg-primary-900 dark:bg-primary-800"
              >
                {submitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Profile
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Confidentiality footer */}
      <div className="flex items-center justify-center gap-2 py-2">
        <svg className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <span className="text-xs text-gray-400 dark:text-gray-500">Your information is encrypted and kept strictly confidential</span>
      </div>
    </div>
  )
}

// ─── Field Components ────────────────────────────────

function FieldLabel({ label, required, hint }) {
  return (
    <div className="mb-2">
      <label className="block text-sm font-medium text-gray-900 dark:text-white">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      {hint && <p className="text-xs mt-0.5 text-gray-400 dark:text-gray-500">{hint}</p>}
    </div>
  )
}

function TextInput({ value, onChange, placeholder, type = 'text', maxLength }) {
  return (
    <input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className="w-full text-sm rounded-lg px-3.5 py-2.5 transition-all focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-primary-400 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white bg-white dark:bg-gray-700"
    />
  )
}

function SelectInput({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      className={`w-full text-sm rounded-lg px-3.5 py-2.5 pr-10 transition-all focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-primary-400 appearance-none bg-no-repeat border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 ${value ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}
      style={{
        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2394A3B8' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
        backgroundPosition: 'right 0.5rem center',
        backgroundSize: '1.5em 1.5em'
      }}
    >
      <option value="">{placeholder || 'Select...'}</option>
      {options.map(opt => {
        const val = typeof opt === 'string' ? opt : opt.value
        const label = typeof opt === 'string' ? opt : opt.label
        return <option key={val} value={val}>{label}</option>
      })}
    </select>
  )
}

function RadioGroup({ value, onChange, options, columns = 'auto' }) {
  const gridClass = columns === 2 ? 'grid grid-cols-1 sm:grid-cols-2 gap-2.5' : 'flex flex-wrap gap-2.5'

  return (
    <div className={gridClass}>
      {options.map(opt => {
        const isSelected = value === opt.value
        return (
          <label
            key={opt.value}
            className={`flex items-center gap-2.5 text-sm cursor-pointer px-4 py-3 rounded-lg transition-all border-[1.5px] text-gray-900 dark:text-white ${
              isSelected
                ? 'border-primary-400 bg-cyan-50 dark:bg-cyan-900/20 shadow-[0_0_0_1px_rgba(138,208,214,0.3)]'
                : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700'
            }`}
          >
            <input
              type="radio"
              checked={isSelected}
              onChange={() => onChange(opt.value)}
              className="sr-only"
            />
            <div
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                isSelected ? 'border-primary-400' : 'border-gray-300 dark:border-gray-500'
              }`}
            >
              {isSelected && (
                <div className="w-2 h-2 rounded-full bg-primary-400" />
              )}
            </div>
            <span className={isSelected ? 'font-medium' : ''}>{opt.label}</span>
          </label>
        )
      })}
    </div>
  )
}

function MultiSelectDropdown({ value = [], onChange, options, placeholder }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = search
    ? options.filter(o => o.toLowerCase().includes(search.toLowerCase()))
    : options

  const toggle = (item) => {
    const next = value.includes(item)
      ? value.filter(v => v !== item)
      : [...value, item]
    onChange(next)
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full text-left text-sm rounded-lg px-3.5 py-2.5 flex items-center justify-between transition-all border-[1.5px] bg-white dark:bg-gray-700 ${
          open ? 'border-primary-400' : 'border-gray-200 dark:border-gray-600'
        } ${value.length ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}
      >
        <span className="truncate">
          {value.length ? `${value.length} selected` : placeholder || 'Select...'}
        </span>
        <svg className={`w-4 h-4 flex-shrink-0 transition-transform text-gray-400 dark:text-gray-500 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => { setOpen(false); setSearch('') }} />
          <div className="absolute z-20 mt-1 w-full rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700">
            <div className="p-2 border-b border-gray-100 dark:border-gray-600">
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full text-sm px-2.5 py-1.5 rounded border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-1 focus:ring-primary-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                autoFocus
              />
            </div>
            <div className="max-h-48 overflow-y-auto py-1">
              {filtered.map(opt => (
                <label
                  key={opt}
                  className="flex items-center gap-2.5 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-gray-900 dark:text-white"
                >
                  <input
                    type="checkbox"
                    checked={value.includes(opt)}
                    onChange={() => toggle(opt)}
                    className="rounded accent-primary-400"
                  />
                  {opt}
                </label>
              ))}
              {filtered.length === 0 && (
                <p className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">No results</p>
              )}
            </div>
          </div>
        </>
      )}
      {/* Selected tags */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map(v => (
            <span
              key={v}
              className="text-xs px-2.5 py-1 rounded-full flex items-center gap-1 transition-colors bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-cyan-100 dark:border-cyan-800"
            >
              {v}
              <button onClick={() => toggle(v)} className="hover:opacity-70 ml-0.5 text-primary-700 dark:text-primary-300">&times;</button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function TextArea({ value, onChange, placeholder, maxLength }) {
  return (
    <div>
      <textarea
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={3}
        className="w-full text-sm rounded-lg px-3.5 py-2.5 resize-none transition-all focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-primary-400 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white bg-white dark:bg-gray-700"
      />
      {maxLength && (
        <p className="text-xs mt-1 text-right text-gray-400 dark:text-gray-500">
          {(value || '').length}/{maxLength}
        </p>
      )}
    </div>
  )
}

// ─── Sections ────────────────────────────────────────

function SectionOrganization({ formData, updateField, subCategoryOptions }) {
  return (
    <div className="space-y-6">
      <div>
        <FieldLabel label="Legal Organization Name" required hint="As registered with your state or federal filings" />
        <TextInput value={formData.name} onChange={v => updateField('name', v)} placeholder="e.g., Acme Corporation" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <FieldLabel label="Industry" required />
          <SelectInput
            value={formData.industry}
            onChange={v => updateField('industry', v)}
            options={INDUSTRY_HIERARCHY.map(i => ({ value: i.value, label: i.label }))}
            placeholder="Select industry..."
          />
        </div>
        {subCategoryOptions.length > 0 && (
          <div>
            <FieldLabel label="Sub-Industry" />
            <SelectInput
              value={formData.industry_sub_category}
              onChange={v => updateField('industry_sub_category', v)}
              options={subCategoryOptions.map(s => ({ value: s.value, label: s.label }))}
              placeholder="Select sub-industry..."
            />
          </div>
        )}
      </div>

      {/* Divider with label */}
      <div className="flex items-center gap-3 pt-2">
        <div className="h-px flex-1 bg-gray-100 dark:bg-gray-700" />
        <span className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Staffing & Budget</span>
        <div className="h-px flex-1 bg-gray-100 dark:bg-gray-700" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div>
          <FieldLabel label="Staffing Size" required hint="Total employee count" />
          <SelectInput value={formData.staffing_size} onChange={v => updateField('staffing_size', v)} options={STAFFING_SIZE_OPTIONS} placeholder="Select range..." />
        </div>
        <div>
          <FieldLabel label="IT Team Size" hint="Dedicated IT headcount" />
          <SelectInput value={formData.it_team_size} onChange={v => updateField('it_team_size', v)} options={IT_TEAM_SIZE_OPTIONS} placeholder="Select range..." />
        </div>
        <div>
          <FieldLabel label="Security Team Size" hint="Dedicated security headcount" />
          <SelectInput value={formData.security_team_size} onChange={v => updateField('security_team_size', v)} options={SECURITY_TEAM_SIZE_OPTIONS} placeholder="Select range..." />
        </div>
      </div>

      <div>
        <FieldLabel label="How is your IT function managed?" hint="This helps us understand your operational model" />
        <RadioGroup value={formData.security_function_model} onChange={v => updateField('security_function_model', v)} options={SECURITY_FUNCTION_MODEL_OPTIONS} />
      </div>

      <div>
        <FieldLabel label="Annual Revenue Range" required />
        <SelectInput value={formData.annual_revenue} onChange={v => updateField('annual_revenue', v)} options={ANNUAL_REVENUE_OPTIONS} placeholder="Select range..." />
      </div>
    </div>
  )
}

function SectionContact({ formData, updateField }) {
  return (
    <div className="space-y-6">
      {/* Info callout */}
      <div className="flex items-start gap-3 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
        <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Your primary contact will receive assessment deliverables and meeting invitations. A secondary contact is optional but recommended.
        </p>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Primary Contact</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <FieldLabel label="Full Name" required />
            <TextInput value={formData.contact_name} onChange={v => updateField('contact_name', v)} placeholder="Jane Smith" />
          </div>
          <div>
            <FieldLabel label="Email Address" required />
            <TextInput value={formData.contact_email} onChange={v => updateField('contact_email', v)} placeholder="jane@company.com" type="email" />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Secondary Contact <span className="normal-case font-normal">(optional)</span></p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <FieldLabel label="Full Name" />
            <TextInput value={formData.secondary_contact_name} onChange={v => updateField('secondary_contact_name', v)} placeholder="John Doe" />
          </div>
          <div>
            <FieldLabel label="Email Address" />
            <TextInput value={formData.secondary_contact_email} onChange={v => updateField('secondary_contact_email', v)} placeholder="john@company.com" type="email" />
          </div>
        </div>
      </div>
    </div>
  )
}

function SectionLocation({ formData, updateField, showInternational }) {
  return (
    <div className="space-y-6">
      <div>
        <FieldLabel label="Operational Headquarters (State)" hint="State where your primary office is located" />
        <SelectInput value={formData.operational_hq} onChange={v => updateField('operational_hq', v)} options={US_STATES.map(s => ({ value: s, label: s }))} placeholder="Select state..." />
      </div>

      <div>
        <FieldLabel label="Geographical Presence" required hint="Scope of your business operations" />
        <RadioGroup value={formData.geographical_presence} onChange={v => updateField('geographical_presence', v)} options={GEOGRAPHICAL_PRESENCE_OPTIONS} />
      </div>

      <div>
        <FieldLabel label="Other Office Locations (US States)" hint="Select all states where you have offices, employees, or significant business activity" />
        <MultiSelectDropdown value={formData.office_location_states || []} onChange={v => updateField('office_location_states', v)} options={US_STATES} placeholder="Select states..." />
      </div>

      <div>
        <FieldLabel label="States Where You Store Resident Data" hint="This determines applicable privacy regulations (CCPA, NYDFS, etc.)" />
        <MultiSelectDropdown value={formData.data_residency_states || []} onChange={v => updateField('data_residency_states', v)} options={US_STATES} placeholder="Select states..." />
      </div>

      {showInternational && (
        <div>
          <FieldLabel label="International Locations" hint="Countries where you have operations, customers, or store data" />
          <TextInput value={formData.international_locations} onChange={v => updateField('international_locations', v)} placeholder="e.g., United Kingdom, Germany, Canada" />
        </div>
      )}
    </div>
  )
}

function SectionBusiness({ formData, updateField, knownFrameworks, customFrameworkInput, setCustomFrameworkInput }) {
  const selectedFrameworks = formData.compliance_frameworks || []
  const customFrameworks = formData.custom_frameworks_raw || []

  // Group known frameworks by category
  const grouped = useMemo(() => {
    const groups = {}
    for (const fw of knownFrameworks) {
      if (!groups[fw.category]) groups[fw.category] = []
      groups[fw.category].push(fw)
    }
    return groups
  }, [knownFrameworks])

  const categoryLabels = {
    regulatory: 'Regulatory',
    certification: 'Certification',
    standard: 'Standard',
    contractual: 'Contractual'
  }

  const toggleFramework = (abbreviation) => {
    const current = [...selectedFrameworks]
    const idx = current.indexOf(abbreviation)
    if (idx >= 0) {
      current.splice(idx, 1)
    } else {
      // Remove "none" if selecting a real framework
      const filtered = current.filter(v => v !== 'none' && v !== 'none-of-the-above')
      filtered.push(abbreviation)
      updateField('compliance_frameworks', filtered)
      return
    }
    updateField('compliance_frameworks', current)
  }

  const addCustomFramework = () => {
    const name = customFrameworkInput.trim()
    if (!name) return
    if (!customFrameworks.includes(name)) {
      updateField('custom_frameworks_raw', [...customFrameworks, name])
    }
    // Also add to selected frameworks list
    if (!selectedFrameworks.includes(name)) {
      updateField('compliance_frameworks', [...selectedFrameworks.filter(v => v !== 'none'), name])
    }
    setCustomFrameworkInput('')
  }

  return (
    <div className="space-y-6">
      <div>
        <FieldLabel label="Primary Business Model" hint="Who are your primary customers?" />
        <RadioGroup value={formData.business_model} onChange={v => updateField('business_model', v)} options={BUSINESS_MODEL_OPTIONS} columns={2} />
      </div>

      {/* Compliance Frameworks */}
      <div>
        <FieldLabel label="Compliance Frameworks & Regulations" hint="Select all regulatory frameworks or compliance requirements that apply to your organization" />
        <div className="space-y-4 mt-2">
          {Object.entries(grouped).map(([category, frameworks]) => (
            <div key={category}>
              <p className="text-xs font-medium uppercase tracking-wider mb-2 text-gray-400 dark:text-gray-500">
                {categoryLabels[category] || category}
              </p>
              <div className="space-y-1.5">
                {frameworks.map(fw => {
                  const isChecked = selectedFrameworks.includes(fw.abbreviation)
                  return (
                    <label
                      key={fw.id}
                      className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all border-[1.5px] ${
                        isChecked
                          ? 'border-primary-400 bg-cyan-50 dark:bg-cyan-900/20'
                          : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleFramework(fw.abbreviation)}
                        className="h-4 w-4 rounded border-gray-300 accent-primary-400"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{fw.abbreviation}</span>
                        <span className="text-xs ml-2 text-gray-500 dark:text-gray-400">{fw.name}</span>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Custom frameworks */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs font-medium uppercase tracking-wider mb-2 text-gray-400 dark:text-gray-500">Other / Unlisted Frameworks</p>

          {customFrameworks.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {customFrameworks.map(name => (
                <span key={name} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
                  {name}
                  <button
                    type="button"
                    onClick={() => {
                      updateField('custom_frameworks_raw', customFrameworks.filter(n => n !== name))
                      updateField('compliance_frameworks', selectedFrameworks.filter(v => v !== name))
                    }}
                    className="ml-0.5 hover:opacity-70"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <input
              type="text"
              value={customFrameworkInput}
              onChange={(e) => setCustomFrameworkInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomFramework() } }}
              placeholder="e.g., CARF, FERPA, NYDFS..."
              className="flex-1 text-sm rounded-lg px-3 py-2 border-[1.5px] border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
            <button
              type="button"
              onClick={addCustomFramework}
              disabled={!customFrameworkInput.trim()}
              className="px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors disabled:opacity-50 bg-primary-400"
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function EngagementMultiSelect({ question, value = [], otherText = '', onChange, onOtherChange, maxSelections = 3 }) {
  const hasOther = value.includes('__other__')
  const standardSelections = value.filter(v => v !== '__other__')

  const toggleOption = (label) => {
    if (label === '__other__') {
      const next = hasOther ? value.filter(v => v !== '__other__') : [...value, '__other__']
      onChange(next)
      if (hasOther) onOtherChange('')
      return
    }
    const isSelected = standardSelections.includes(label)
    if (isSelected) {
      onChange(value.filter(v => v !== label))
    } else {
      if (value.length >= maxSelections) return
      onChange([...value, label])
    }
  }

  const atLimit = value.length >= maxSelections

  return (
    <div className="space-y-3">
      <div className="grid gap-2.5 md:grid-cols-2">
        {question.options.map((opt) => {
          const isSelected = standardSelections.includes(opt.label)
          const disabled = !isSelected && atLimit
          return (
            <button
              key={opt.label}
              type="button"
              onClick={() => toggleOption(opt.label)}
              disabled={disabled}
              className={`p-3.5 rounded-lg text-left text-sm transition-all flex items-start gap-3 border-[1.5px] ${
                isSelected
                  ? 'border-primary-400 bg-cyan-50 dark:bg-cyan-900/20 shadow-[0_0_0_1px_rgba(138,208,214,0.3)]'
                  : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700'
              } ${disabled ? 'text-gray-400 dark:text-gray-500 opacity-50 cursor-not-allowed' : 'text-gray-900 dark:text-white cursor-pointer'}`}
            >
              <div
                className={`rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  isSelected ? 'border-primary-400 bg-primary-400' : 'border-gray-300 dark:border-gray-500 bg-transparent'
                }`}
                style={{ width: '18px', height: '18px' }}
              >
                {isSelected && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className={isSelected ? 'font-medium' : ''}>{opt.label}</span>
            </button>
          )
        })}

        {/* Other option */}
        <button
          type="button"
          onClick={() => toggleOption('__other__')}
          disabled={!hasOther && atLimit}
          className={`p-3.5 rounded-lg text-left text-sm transition-all flex items-start gap-3 border-[1.5px] ${
            hasOther
              ? 'border-primary-400 border-solid bg-cyan-50 dark:bg-cyan-900/20'
              : 'border-gray-200 dark:border-gray-600 border-dashed bg-white dark:bg-gray-700'
          } ${(!hasOther && atLimit) ? 'text-gray-400 dark:text-gray-500 opacity-50 cursor-not-allowed' : 'text-gray-900 dark:text-white cursor-pointer'}`}
        >
          <div
            className={`rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
              hasOther ? 'border-primary-400 bg-primary-400' : 'border-gray-300 dark:border-gray-500 bg-transparent'
            }`}
            style={{ width: '18px', height: '18px' }}
          >
            {hasOther && (
              <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <span className={hasOther ? 'font-medium' : ''}>Other</span>
        </button>
      </div>

      {hasOther && (
        <div className="ml-7">
          <TextArea
            value={otherText}
            onChange={onOtherChange}
            placeholder="Please describe..."
            maxLength={300}
          />
        </div>
      )}

      <p className="text-xs text-right text-gray-400 dark:text-gray-500">
        {value.length} of {maxSelections} selected
      </p>
    </div>
  )
}

function EngagementSingleSelect({ question, value, onChange }) {
  return (
    <div className="grid gap-2.5 md:grid-cols-2">
      {question.options.map((opt) => {
        const isSelected = value === opt.label
        return (
          <button
            key={opt.label}
            type="button"
            onClick={() => onChange(opt.label)}
            className={`p-3.5 rounded-lg text-left text-sm transition-all flex items-start gap-3 border-[1.5px] text-gray-900 dark:text-white ${
              isSelected
                ? 'border-primary-400 bg-cyan-50 dark:bg-cyan-900/20 shadow-[0_0_0_1px_rgba(138,208,214,0.3)]'
                : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700'
            }`}
          >
            <div
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                isSelected ? 'border-primary-400' : 'border-gray-300 dark:border-gray-500'
              }`}
            >
              {isSelected && (
                <div className="w-2 h-2 rounded-full bg-primary-400" />
              )}
            </div>
            <span className={isSelected ? 'font-medium' : ''}>{opt.label}</span>
          </button>
        )
      })}
    </div>
  )
}

function SectionEngagement({ engagementData, updateEngagement }) {
  const ed1 = ED_QUESTIONS.find(q => q.id === 'ed-1')
  const ed2 = ED_QUESTIONS.find(q => q.id === 'ed-2')
  const ed3 = ED_QUESTIONS.find(q => q.id === 'ed-3')

  const answeredCount = [
    Array.isArray(engagementData.engagement_drivers) && engagementData.engagement_drivers.length > 0,
    !!engagementData.target_timeline,
    Array.isArray(engagementData.success_criteria) && engagementData.success_criteria.length > 0
  ].filter(Boolean).length

  return (
    <div className="space-y-7">
      {/* Hero callout — matches admin EngagementDriverSection */}
      <div className="rounded-xl p-6 text-white" style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)' }}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-bold mb-2">Let's start with why</h3>
            <p className="text-blue-100 max-w-lg text-sm leading-relaxed">
              Understanding what brings you here helps us tailor recommendations to your specific situation.
              This context shapes how we prioritize findings and frame our guidance.
            </p>
          </div>
          <div className="flex-shrink-0 rounded-lg px-4 py-2 text-center bg-white/20">
            <span className="text-2xl font-bold">{answeredCount}/{ED_QUESTIONS.length}</span>
            <p className="text-xs text-blue-100">answered</p>
          </div>
        </div>
      </div>

      {/* Note about scoring */}
      <div className="flex items-start gap-3 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
        <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-400 dark:text-gray-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          <strong className="text-gray-600 dark:text-gray-300">Note:</strong> These questions help shape your roadmap and report framing but do not affect your maturity scores.
          Your security assessment results are based solely on your control implementation.
        </p>
      </div>

      {ed1 && (
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-500">
              <span className="text-xs font-bold">1</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{ed1.question}</p>
              <p className="text-xs mt-0.5 text-gray-400 dark:text-gray-500">Select up to {ed1.maxSelections || 3} — these affect how we prioritize recommendations</p>
            </div>
          </div>
          <div className="ml-10">
            <EngagementMultiSelect
              question={ed1}
              value={engagementData.engagement_drivers || []}
              otherText={engagementData.engagement_drivers_other || ''}
              onChange={v => updateEngagement('engagement_drivers', v)}
              onOtherChange={v => updateEngagement('engagement_drivers_other', v)}
              maxSelections={ed1.maxSelections || 3}
            />
          </div>
        </div>
      )}

      {ed2 && (
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-500">
              <span className="text-xs font-bold">2</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{ed2.question}</p>
              <p className="text-xs mt-0.5 text-gray-400 dark:text-gray-500">This helps us phase initiatives and set realistic expectations</p>
            </div>
          </div>
          <div className="ml-10">
            <EngagementSingleSelect
              question={ed2}
              value={engagementData.target_timeline || ''}
              onChange={v => updateEngagement('target_timeline', v)}
            />
          </div>
        </div>
      )}

      {ed3 && (
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-500">
              <span className="text-xs font-bold">3</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{ed3.question}</p>
              <p className="text-xs mt-0.5 text-gray-400 dark:text-gray-500">Select up to {ed3.maxSelections || 3} — ensures deliverables align with your expectations</p>
            </div>
          </div>
          <div className="ml-10">
            <EngagementMultiSelect
              question={ed3}
              value={engagementData.success_criteria || []}
              otherText={engagementData.success_criteria_other || ''}
              onChange={v => updateEngagement('success_criteria', v)}
              onOtherChange={v => updateEngagement('success_criteria_other', v)}
              maxSelections={ed3.maxSelections || 3}
            />
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Read-Only Summary ───────────────────────────────

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start gap-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
      <span className="text-sm flex-shrink-0 w-40 text-gray-500 dark:text-gray-400">{label}</span>
      <span className={`text-sm font-medium ${value ? 'text-gray-900 dark:text-white' : 'text-gray-300 dark:text-gray-600'}`}>
        {Array.isArray(value) ? (value.length > 0 ? value.join(', ') : 'Not provided') : (value || 'Not provided')}
      </span>
    </div>
  )
}

function ReadOnlySummary({ formData, engagementData, subCategoryOptions }) {
  const subLabel = subCategoryOptions.find(s => s.value === formData.industry_sub_category)?.label

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-xs font-semibold mb-4 uppercase tracking-wider text-gray-400 dark:text-gray-500">Organization Information</h3>
        <SummaryRow label="Organization Name" value={formData.name} />
        <SummaryRow label="Industry" value={formData.industry} />
        {subLabel && <SummaryRow label="Sub-Industry" value={subLabel} />}
        <SummaryRow label="Staffing Size" value={STAFFING_SIZE_OPTIONS.find(o => o.value === formData.staffing_size)?.label || formData.staffing_size} />
        <SummaryRow label="IT Team Size" value={IT_TEAM_SIZE_OPTIONS.find(o => o.value === formData.it_team_size)?.label || formData.it_team_size} />
        <SummaryRow label="Security Team Size" value={formData.security_team_size} />
        <SummaryRow label="Security Function" value={SECURITY_FUNCTION_MODEL_OPTIONS.find(o => o.value === formData.security_function_model)?.label || formData.security_function_model} />
        <SummaryRow label="Annual Revenue" value={formData.annual_revenue} />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-xs font-semibold mb-4 uppercase tracking-wider text-gray-400 dark:text-gray-500">Contact Information</h3>
        <SummaryRow label="Primary Contact" value={formData.contact_name} />
        <SummaryRow label="Primary Email" value={formData.contact_email} />
        <SummaryRow label="Secondary Contact" value={formData.secondary_contact_name} />
        <SummaryRow label="Secondary Email" value={formData.secondary_contact_email} />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-xs font-semibold mb-4 uppercase tracking-wider text-gray-400 dark:text-gray-500">Locations & Data Residency</h3>
        <SummaryRow label="Headquarters" value={formData.operational_hq} />
        <SummaryRow label="Geo Presence" value={GEOGRAPHICAL_PRESENCE_OPTIONS.find(o => o.value === formData.geographical_presence)?.label || formData.geographical_presence} />
        <SummaryRow label="Office States" value={formData.office_location_states} />
        <SummaryRow label="Data Residency" value={formData.data_residency_states} />
        {formData.international_locations && <SummaryRow label="International" value={formData.international_locations} />}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-xs font-semibold mb-4 uppercase tracking-wider text-gray-400 dark:text-gray-500">Business & Engagement Context</h3>
        <SummaryRow label="Business Model" value={BUSINESS_MODEL_OPTIONS.find(o => o.value === formData.business_model)?.label || formData.business_model} />
        <SummaryRow label="Engagement Drivers" value={(() => {
          const drivers = engagementData.engagement_drivers || []
          const display = drivers.filter(v => v !== '__other__')
          if (drivers.includes('__other__') && engagementData.engagement_drivers_other) {
            display.push(`Other: ${engagementData.engagement_drivers_other}`)
          }
          return display
        })()} />
        <SummaryRow label="Target Timeline" value={engagementData.target_timeline} />
        <SummaryRow label="Success Criteria" value={(() => {
          const criteria = engagementData.success_criteria || []
          const display = criteria.filter(v => v !== '__other__')
          if (criteria.includes('__other__') && engagementData.success_criteria_other) {
            display.push(`Other: ${engagementData.success_criteria_other}`)
          }
          return display
        })()} />
      </div>
    </div>
  )
}

export default PortalIntake
